import { useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Button, Input, Spinner } from 'reactstrap';
import { BiArrowBack, BiCalendar, BiCheckCircle, BiPlus } from 'react-icons/bi';
import { toast } from 'react-toastify';
import { updateJobStatus } from '../api/jobsApi';
import { updateJobType } from '../api/insightsApi';
import useJob from '../hooks/useJob';
import useExpensesByJob from '../hooks/useExpensesByJob';
import useInvoicesByJob from '../hooks/useInvoicesByJob';
import CreateExpenseModal from '../components/expense/createExpenseModal';
import ExpenseRow from '../components/expense/expenseRow';
import ScanJobsBanner from '../components/expense/scanJobsBanner';
import CreateInvoiceModal from '../components/invoice/createInvoiceModal';
import InvoiceRow from '../components/invoice/invoiceRow';
import JobKpiStrip from '../components/job/jobKpiStrip';
import JobCostChart from '../components/job/jobCostChart';
import JobExpensePieChart from '../components/job/jobExpensePieChart';
import JobInsightsSection from '../components/job/JobInsightsSection';
import RateJobModal from '../components/job/RateJobModal';
import JobAssignmentsCard from '../components/job/JobAssignmentsCard';
import useJobTypes from '../hooks/useJobTypes';
import { useIsOrgManagerUp } from '../hooks/useOrgRole';
import { formatDate } from '../utils/formatters';

export default function ViewJobPage() {
    const { id: idParam } = useParams();
    const id = Number(idParam);

    // `dirtyRef` is plumbed through useJob (same contract as useCustomer) for
    // a future JobDetailsCard with debounced autosave. Until that lands the
    // job page is read-only and the ref stays false.
    const dirtyRef = useRef(false);
    const { data: job, isLoading, error } = useJob(id, dirtyRef);

    const [addExpenseOpen, setAddExpenseOpen] = useState(false);
    const [addInvoiceOpen, setAddInvoiceOpen] = useState(false);
    const [rateModalOpen, setRateModalOpen] = useState(false);
    const isManagerUp = useIsOrgManagerUp();
    const expenses = useExpensesByJob(id);
    // Workers 403 on GetInvoicesByJob (Manager+ policy) and don't have an
    // Invoices section anyway — gate the fetch behind the role so the
    // SignalR hub subscription doesn't fire either.
    const invoices = useInvoicesByJob(id, isManagerUp === true);
    const { data: jobTypes = [] } = useJobTypes();

    if (isLoading) {
        return (
            <div className="d-flex justify-content-center py-5">
                <Spinner />
            </div>
        );
    }

    // 404 covers both "doesn't exist" and "belongs to a different tenant" —
    // don't leak which via the message.
    if (error?.status === 404 || (!job && !isLoading)) {
        return (
            <div>
                <Link to="/jobs" className="text-decoration-none d-inline-flex align-items-center gap-1 mb-3">
                    <BiArrowBack /> Jobs
                </Link>
                <div className="surface text-center py-5">
                    <h4 className="mb-2">Job not found</h4>
                    <p className="text-muted mb-3">
                        This job doesn't exist or you don't have access to it.
                    </p>
                    <Link to="/jobs">
                        <Button color="primary">Back to Jobs</Button>
                    </Link>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div>
                <Link to="/jobs" className="text-decoration-none d-inline-flex align-items-center gap-1 mb-3">
                    <BiArrowBack /> Jobs
                </Link>
                <div className="surface text-center py-5 text-danger">
                    Failed to load job. Please try again.
                </div>
            </div>
        );
    }

    const startFmt = formatDate(job.startDate);
    const endFmt   = formatDate(job.endDate);
    const hasDates = startFmt || endFmt;

    return (
        <div>
            <Link to="/jobs" className="text-decoration-none d-inline-flex align-items-center gap-1 mb-2">
                <BiArrowBack /> Jobs
            </Link>
            <div className="d-flex justify-content-between align-items-start gap-3 mb-4">
                <div>
                    <h1 className="fw-bold mb-1">{job.title || 'Untitled job'}</h1>
                    <p className="text-muted mb-1">
                        {job.jobNumber && <span>#{job.jobNumber} · </span>}
                        {/* Manager+ get a link through to the customer
                            detail page; Workers see the name as plain
                            text since /customers/:id is gated behind
                            ManagerUp and would 403 on them. */}
                        {job.customerName && (
                            isManagerUp ? (
                                <Link to={`/customers/${job.customerId}`}>
                                    {job.customerName}
                                </Link>
                            ) : (
                                <span>{job.customerName}</span>
                            )
                        )}
                    </p>
                    {/* Date range next to the header keeps it visible without
                        spending a whole surface card on it. Hidden entirely
                        when neither date is set — no point in an empty chip. */}
                    {hasDates && (
                        <div className="text-muted small d-inline-flex align-items-center gap-1">
                            <BiCalendar />
                            <span>{startFmt || '—'} → {endFmt || '—'}</span>
                        </div>
                    )}
                </div>
                <div className="d-flex align-items-center gap-2">
                    {/* Manager+ get the action controls; Workers see status
                        as a read-only chip so the field stays useful at a
                        glance without giving them a way to flip the job's
                        state from the office surface. */}
                    {isManagerUp ? (
                        <>
                            {job.status !== 'Completed' && (
                                <Button
                                    color="success"
                                    size="sm"
                                    className="d-inline-flex align-items-center gap-1"
                                    onClick={async () => {
                                        try {
                                            await updateJobStatus(id, 'Completed');
                                            if (!job.worthwhilenessRating) setRateModalOpen(true);
                                        } catch (err) {
                                            toast.error(err?.message ?? 'Failed to complete job.');
                                        }
                                    }}
                                >
                                    <BiCheckCircle /> Complete Job
                                </Button>
                            )}
                            <Input type="select" bsSize="sm"
                                value={job.status}
                                onChange={async (e) => {
                                    const newStatus = e.target.value;
                                    try {
                                        await updateJobStatus(id, newStatus);
                                        if (newStatus === 'Completed' && !job.worthwhilenessRating) {
                                            setRateModalOpen(true);
                                        }
                                    } catch (err) {
                                        toast.error(err?.message ?? 'Failed to update status.');
                                    }
                                }}
                                style={{ width: 'auto' }}
                            >
                                {['Open', 'In Progress', 'On Hold', 'Completed', 'Cancelled'].map(s =>
                                    <option key={s} value={s}>{s}</option>
                                )}
                            </Input>
                        </>
                    ) : (
                        <span
                            className="badge"
                            style={{
                                backgroundColor: 'var(--bs-tertiary-bg)',
                                color: 'var(--bs-body-color)',
                                fontWeight: 600,
                                padding: '6px 12px',
                                borderRadius: 12,
                                fontSize: '0.8rem',
                                border: '1px solid var(--bs-border-color)',
                            }}
                        >
                            {job.status}
                        </span>
                    )}
                </div>
            </div>

            {/* Manager+ surfaces — financials, insights, job-type editor,
                crew roster, charts and Invoices. Workers see only the
                operational essentials (description, scan banner, their
                own expenses) so the page stays focused on what they can
                actually act on in the field. */}
            {isManagerUp && <JobInsightsSection jobId={id} />}

            {isManagerUp && <JobKpiStrip job={job} />}

            {/* Description card. Workers see it read-only; the Job Type
                editor is Manager+ only. */}
            {(job.description || isManagerUp) && (
                <div className="surface p-3 mb-4">
                    {job.description && (
                        <>
                            <div className="text-muted small">Description</div>
                            <div className={isManagerUp ? 'mb-3' : ''}>{job.description}</div>
                        </>
                    )}
                    {isManagerUp && (
                        <>
                            <div className="text-muted small mb-1">Job Type</div>
                            <Input type="select" bsSize="sm"
                                value={job.jobType ?? ''}
                                onChange={async (e) => {
                                    try {
                                        await updateJobType(id, e.target.value || null);
                                    } catch (err) {
                                        toast.error(err?.message ?? 'Failed to update job type.');
                                    }
                                }}
                                style={{ width: 'auto', minWidth: 200 }}
                            >
                                <option value="">-- Select type --</option>
                                {jobTypes.map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
                                {/* If the job's current type was deactivated since
                                    it was set, surface it so the dropdown still
                                    renders the correct value (matches by string
                                    on the row, not by ID). */}
                                {job.jobType && !jobTypes.some(t => t.name === job.jobType) && (
                                    <option key="legacy" value={job.jobType}>{job.jobType} (inactive)</option>
                                )}
                            </Input>
                        </>
                    )}
                </div>
            )}

            {isManagerUp && <JobAssignmentsCard jobId={id} />}

            {/* Charts row — bar chart (est vs actual) on the left, expense
                category donut on the right. The pie chart only renders when
                there are expenses, so the bar chart expands to full width
                when the job has no expenses yet. */}
            {isManagerUp && (
                <div className="row g-3 mb-4">
                    <div className={expenses.data?.length ? 'col-md-6' : 'col-12'}>
                        <JobCostChart job={job} />
                    </div>
                    {expenses.data?.length > 0 && (
                        <div className="col-md-6">
                            <JobExpensePieChart expenses={expenses.data} currency={job.currency} />
                        </div>
                    )}
                </div>
            )}

            {/* One banner for both flows — the server-side scan-jobs table is
                shared (TargetType discriminates), so a single ScanJobsBanner
                covers both sections and handles Fill-manually routing via
                targetType. */}
            <ScanJobsBanner jobId={id} currency={job.currency} />

            {isManagerUp && (
                <>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h2 className="fw-semibold fs-4 mb-0">Invoices</h2>
                        <Button color="primary" size="sm" onClick={() => setAddInvoiceOpen(true)}
                                className="d-inline-flex align-items-center gap-1">
                            <BiPlus /> Add Invoice
                        </Button>
                    </div>

                    <div className="mb-4">
                        {invoices.isLoading ? (
                            <div className="d-flex justify-content-center py-4">
                                <Spinner />
                            </div>
                        ) : invoices.error ? (
                            <div className="surface text-center py-4 text-danger">
                                Failed to load invoices.
                            </div>
                        ) : invoices.data?.length > 0 ? (
                            invoices.data.map(inv => (
                                <InvoiceRow key={inv.id} invoice={inv} currency={job.currency} />
                            ))
                        ) : (
                            <div className="surface text-center py-4 text-muted">
                                No invoices yet. Use "Add Invoice" above to record one.
                            </div>
                        )}
                    </div>
                </>
            )}

            <div className="d-flex justify-content-between align-items-center mb-3">
                <h2 className="fw-semibold fs-4 mb-0">Expenses</h2>
                <Button color="primary" size="sm" onClick={() => setAddExpenseOpen(true)}
                        className="d-inline-flex align-items-center gap-1">
                    <BiPlus /> Add Expense
                </Button>
            </div>

            <div className="mb-4">
                {expenses.isLoading ? (
                    <div className="d-flex justify-content-center py-4">
                        <Spinner />
                    </div>
                ) : expenses.error ? (
                    <div className="surface text-center py-4 text-danger">
                        Failed to load expenses.
                    </div>
                ) : expenses.data?.length > 0 ? (
                    expenses.data.map(exp => (
                        <ExpenseRow key={exp.id} expense={exp} currency={job.currency} />
                    ))
                ) : (
                    <div className="surface text-center py-4 text-muted">
                        No expenses yet. Use "Add Expense" above to record one.
                    </div>
                )}
            </div>

            <CreateExpenseModal
                isOpen={addExpenseOpen}
                onClose={() => setAddExpenseOpen(false)}
                jobId={id}
                currency={job.currency}
            />

            <CreateInvoiceModal
                isOpen={addInvoiceOpen}
                onClose={() => setAddInvoiceOpen(false)}
                jobId={id}
                currency={job.currency}
            />

            <RateJobModal
                isOpen={rateModalOpen}
                onClose={() => setRateModalOpen(false)}
                jobId={id}
            />
        </div>
    );
}
